import { useState } from "react";
import { FiCopy, FiCheck, FiX } from "react-icons/fi";
import { useAuth } from "../../../auth/AuthProvider";
import { setup2fa, verify2fa, type Setup2faResponse } from "../../../api/auth";

interface Props {
    onClose: () => void;
    onEnabled: () => void;
}

type Step = "qr" | "recovery" | "verify";

export default function TotpSetupModal({ onClose, onEnabled }: Props) {
    const { token } = useAuth();
    const [step, setStep] = useState<Step>("qr");
    const [setupData, setSetupData] = useState<Setup2faResponse | null>(null);
    const [totpCode, setTotpCode] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [codesCopied, setCodesCopied] = useState(false);

    // Start setup on mount
    useState(() => {
        (async () => {
            setLoading(true);
            try {
                const data = await setup2fa(token!);
                setSetupData(data);
            } catch (err: any) {
                setError(err.message || "Failed to start 2FA setup");
            } finally {
                setLoading(false);
            }
        })();
    });

    const copySecret = () => {
        if (setupData) {
            navigator.clipboard.writeText(setupData.secret);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const copyRecoveryCodes = () => {
        if (setupData) {
            navigator.clipboard.writeText(setupData.recovery_codes.join("\n"));
            setCodesCopied(true);
            setTimeout(() => setCodesCopied(false), 2000);
        }
    };

    const handleVerify = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        if (totpCode.length !== 6) {
            setError("Enter a 6-digit code from your authenticator");
            return;
        }
        setLoading(true);
        try {
            await verify2fa(token!, totpCode);
            onEnabled();
        } catch (err: any) {
            setError(err.message || "Verification failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="w-full max-w-lg rounded-xl bg-surface-700 border border-border p-6 shadow-xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-h4 text-text-primary">Set Up Two-Factor Authentication</h3>
                    <button onClick={onClose} className="text-text-muted hover:text-text-primary">
                        <FiX className="w-5 h-5" />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-3 rounded-lg bg-health-500/10 border border-health-500/30">
                        <p className="text-body-sm text-health-400">{error}</p>
                    </div>
                )}

                {loading && !setupData && (
                    <div className="py-8 text-center text-text-muted">Setting up...</div>
                )}

                {/* Step 1: QR Code */}
                {step === "qr" && setupData && (
                    <div className="space-y-4">
                        <p className="text-body-sm text-text-secondary">
                            Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.)
                        </p>
                        <div className="flex justify-center bg-white rounded-lg p-4">
                            <img
                                src={`data:image/png;base64,${setupData.qr_code_base64}`}
                                alt="TOTP QR Code"
                                className="w-48 h-48"
                            />
                        </div>
                        <div className="text-center">
                            <p className="text-caption text-text-muted mb-1">Or enter this code manually:</p>
                            <div className="flex items-center justify-center gap-2">
                                <code className="px-3 py-1.5 bg-surface-600 rounded text-body-sm text-text-primary font-mono tracking-wider">
                                    {setupData.secret}
                                </code>
                                <button onClick={copySecret} className="text-text-muted hover:text-quantum-400">
                                    {copied ? <FiCheck className="w-4 h-4 text-ecosystem-400" /> : <FiCopy className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        <button
                            onClick={() => setStep("recovery")}
                            className="btn-primary w-full"
                        >
                            Next: Save Recovery Codes
                        </button>
                    </div>
                )}

                {/* Step 2: Recovery Codes */}
                {step === "recovery" && setupData && (
                    <div className="space-y-4">
                        <p className="text-body-sm text-text-secondary">
                            Save these recovery codes in a safe place. Each can be used once if you lose access to your authenticator.
                        </p>
                        <div className="bg-surface-600 rounded-lg p-4">
                            <div className="grid grid-cols-2 gap-2">
                                {setupData.recovery_codes.map((code, i) => (
                                    <code key={i} className="text-body-sm font-mono text-text-primary">
                                        {code}
                                    </code>
                                ))}
                            </div>
                        </div>
                        <button
                            onClick={copyRecoveryCodes}
                            className="btn-secondary w-full flex items-center justify-center gap-2"
                        >
                            {codesCopied ? <><FiCheck /> Copied!</> : <><FiCopy /> Copy Recovery Codes</>}
                        </button>
                        <button
                            onClick={() => setStep("verify")}
                            className="btn-primary w-full"
                        >
                            Next: Verify Setup
                        </button>
                    </div>
                )}

                {/* Step 3: Verify */}
                {step === "verify" && (
                    <form onSubmit={handleVerify} className="space-y-4">
                        <p className="text-body-sm text-text-secondary">
                            Enter the 6-digit code from your authenticator app to complete setup.
                        </p>
                        <div>
                            <label className="input-label">Verification Code</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                value={totpCode}
                                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                                className="input text-center text-2xl font-mono tracking-[0.5em]"
                                placeholder="000000"
                                autoFocus
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading || totpCode.length !== 6}
                            className="btn-primary w-full"
                        >
                            {loading ? "Verifying..." : "Enable 2FA"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
